/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/api/orders.ts
 * ---------------------------------------------------------------------------
 * Client “fino” para as APIs de **Pedidos de Venda (Orders)**.
 *
 * Padrões adotados
 * - Usa o axios central (src/lib/api.ts) → baseURL + Authorization + refresh.
 * - Evita “/api/api”: os paths aqui são relativos à raiz da API (/api/...).
 * - Paginação defensiva: se o backend retornar array puro, normalizamos para
 *   {results: [...]} mantendo compat com telas que esperam Page<T>.
 * - Nenhum parâmetro “undefined” é enviado (evita TS2322/TS2345).
 *
 * Endpoints esperados (orders app)
 *   GET    /api/orders/orders/
 *   GET    /api/orders/orders/{id}/
 *   PUT    /api/orders/orders/{id}/
 *   DELETE /api/orders/orders/{id}/
 *   POST   /api/orders/orders/{id}/release/     (se existir)
 *   POST   /api/orders/orders/{id}/evaluate/    (se existir)
 *   (Fallbacks 404 tratados abaixo)
 * ========================================================================== */

import api from "@/lib/api"

// ---------------------------------------------------------------------------
// Tipagens mínimas (mantemos flexibilidade enquanto o backend evolui)
// ---------------------------------------------------------------------------
export type OrderStatus = string
// Exemplos: 'PENDING' | 'RELEASED' | 'ALLOCATED' | 'PICKED' | 'DONE' | ...

export type Order = {
    id: number
    ref_code?: string
    status?: OrderStatus
    customer_code?: string
    customer_name?: string
    created_at?: string
    updated_at?: string
    // …campos adicionais podem aparecer sem quebrar o front
}

export type Page<T> = {
    count?: number
    next?: string | null
    previous?: string | null
    results: T[]
}

export type ListOrdersParams = {
    search?: string
    status?: OrderStatus
    customer?: string // código/ID conforme a API
    page?: number
    page_size?: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Remove chaves com `undefined`/vazias (não envia valores indefinidos). */
function clean<T extends Record<string, any>>(obj: T): Partial<T> {
    const out: Partial<T> = {} as Partial<T>
    for (const [k, v] of Object.entries(obj || {})) {
        if (v !== undefined && v !== null && v !== "") {
            ; (out as any)[k] = v
        }
    }
    return out
}

/** Normaliza resposta para Page<T> quando a API pode devolver array puro. */
function normalizePage<T>(data: any): Page<T> {
    if (Array.isArray(data)) {
        return { results: data as T[], count: data.length, next: null, previous: null }
    }
    if (Array.isArray(data?.results)) return data as Page<T>
    return { results: [], count: 0, next: null, previous: null }
}

const BASE = "/orders/orders/"

// ---------------------------------------------------------------------------
// Listagem
// ---------------------------------------------------------------------------
export async function listOrders(params: ListOrdersParams = {}): Promise<Page<Order>> {
    const { data } = await api.get(BASE, { params: clean(params) })
    return normalizePage<Order>(data)
}

/** Açúcar: lista apenas pedidos com status PENDING. */
export async function listPendingOrders(
    params: Omit<ListOrdersParams, "status"> = {}
): Promise<Page<Order>> {
    return listOrders({ ...params, status: "PENDING" })
}

// ---------------------------------------------------------------------------
// CRUD básico
// ---------------------------------------------------------------------------
export async function getOrder(id: number): Promise<Order> {
    const { data } = await api.get(`${BASE}${id}/`)
    return data as Order
}

export async function updateOrder(id: number, payload: Partial<Order>): Promise<Order> {
    const { data } = await api.put(`${BASE}${id}/`, payload)
    return data as Order
}

export async function deleteOrder(id: number): Promise<void> {
    await api.delete(`${BASE}${id}/`)
}

// ---------------------------------------------------------------------------
// Ações (com fallback caso o endpoint específico não exista ainda)
// ---------------------------------------------------------------------------
/**
 * Tenta liberar o pedido (ex.: cria tasks/picking). Se a rota /release/ não
 * existir (404), faz fallback para simples atualização de status (RELEASED).
 */
export async function releaseOrder(id: number): Promise<Order> {
    try {
        const { data } = await api.post(`${BASE}${id}/release/`, {})
        return data as Order
    } catch (_err: any) {
        // Fallback “leve” (mantém a tela funcional mesmo sem a rota especial)
        const { data } = await api.put(`${BASE}${id}/`, { status: "RELEASED" })
        return data as Order
    }
}

/**
 * “Avalia” o pedido (ex.: verifica estoque, regras de cliente, etc.).
 * Se /evaluate/ não existir, apenas retorna o pedido atual (no-op).
 */
export async function evaluateOrder(id: number): Promise<Order> {
    try {
        const { data } = await api.post(`${BASE}${id}/evaluate/`, {})
        return data as Order
    } catch {
        // No-op: mantém compatibilidade com telas que esperam chamar evaluate()
        return getOrder(id)
    }
}
